// Plan ahead: the four-week planner (SCENARIOS D10, PLAN-2026-10-03-research-
// tools.md §8, B-252). A team's roster by position over the coming weeks —
// each cell the club's opponent, tinted by the matchup cue from Standard; a
// bye shaded; a week not on the calendar yet a dash — and under each position
// how many can play each week against how many it starts.
//
// The weeks ahead, by position, so a manager can plan for thin weeks. The
// short week is the point of the grid, so it is the row the
// eye lands on: the count, in the warning color when the position cannot fill
// its slots. Nothing on it says whom to add (tools, not instructions).
//
// A week's header opens that week's lineup (D4); a name opens the card.

import type { PlanGroup, PlanView } from '@fsp/domain';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtLeast } from '../data/detail';
import { useLeagueBase, useMyTeamId, usePlan } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { EmptyState, positionLabel, PositionPill, ProTeamCode, StatusBadge } from '../kit';
import { Caption } from '../kit/ui';
import { DashboardScreen } from '../templates/DashboardScreen';
import { useTier } from '../shell/useTier';
import { FromLeague } from './shared/FromLeague';
import { matchupSentence } from './shared/MatchupChip';
import { Term } from '../kit/Why';
import { TeamName } from './shared/Names';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import '../styles/screens/plan.css';
import { clubLabel } from '../sports/registry';

export const PLAN_WEEKS = 4;

export function PlanScreen() {
  const [params] = useSearchParams();
  const mine = useMyTeamId();
  const teamId = params.get('team') ?? mine;
  const source = useLeagueSource();
  const snapshot = usePlan(source === 'api' ? teamId : null, PLAN_WEEKS);
  if (source === 'sim') {
    return (
      <EmptyState>
        Plan ahead reads a real league’s schedule. The tour plays one week of games, so there is
        nothing ahead to plan.
      </EmptyState>
    );
  }
  if (teamId === null) return <EmptyState>You have no team in this league.</EmptyState>;
  return <FromLeague snapshot={snapshot}>{(view) => <PlanBody view={view} />}</FromLeague>;
}

/** What "Can play" counts, said where it is (the page-by-page review of 2026-10-11). */
const CAN_PLAY =
  'How many of his position have a game that week and are not out, against how many it starts. Short means a spot nobody can fill yet.';

/**
 * THE KEY TO THE GRID, said once above it: what a cell is, what @ means, and
 * from Standard what the two tints mean. The tints were never named, so green
 * and red read as good and bad with nothing to say whose (the page-by-page
 * review of 2026-10-11). Facts, never advice: which teams have allowed more.
 */
export function PlanKey({ tints }: { tints: boolean }) {
  return (
    <div className="fsp-plan-key fsp-sub">
      {/* EACH PLAYER'S, said so: under a team's name "his opponent" read
          as the manager's opponent that week. */}
      <span>Each week shows the player's opponent; @ is an away game.</span>
      {tints ? (
        <>
          <span className="fsp-plan-key-item">
            <span className="fsp-plan-swatch" data-band="most" aria-hidden />
            allowed the most to the player's position
          </span>
          <span className="fsp-plan-key-item">
            <span className="fsp-plan-swatch" data-band="fewest" aria-hidden />
            allowed the fewest
          </span>
        </>
      ) : null}
    </div>
  );
}

function PlanBody({ view }: { view: PlanView }) {
  const tier = useTier();
  const tints = useAtLeast('standard');
  const [card, setCard] = useState<PlayerCardData | null>(null);
  // DashboardScreen's own columns, so the groups spread across all of them.
  const columns = tier === 'phone' ? 1 : tier === 'tablet' ? 2 : 3;
  if (view.weeks.length === 0) {
    return <EmptyState>The season has no weeks left to plan.</EmptyState>;
  }
  return (
    <>
      <DashboardScreen
        widgets={[
          {
            id: 'head',
            pinned: true,
            content: (
              <div>
                <div className="fsp-plan-head">
                  <Caption>Plan ahead</Caption>
                  <span className="fsp-plan-team">
                    <TeamName team={view.team} />
                  </span>
                </div>
                <PlanKey tints={tints} />
              </div>
            ),
          },
          ...view.groups
            .filter((g) => g.rows.length > 0 || g.starting > 0)
            .map((g, n) => ({
              id: `group-${g.position}`,
              column: ((n % columns) + 1) as 1 | 2 | 3,
              content: <PlanGroupTable group={g} view={view} onOpen={setCard} />,
            })),
        ]}
      />
      <PlayerCard data={card} onClose={() => setCard(null)} />
    </>
  );
}

/** One position's weeks ahead: its players by week, and who can play (B-252). My Team's Ahead panel draws the same (B-316). */
export function PlanGroupTable({
  group,
  view,
  onOpen,
}: {
  group: PlanGroup;
  view: PlanView;
  onOpen: (card: PlayerCardData) => void;
}) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const counts = useAtLeast('standard');
  const label = positionLabel(group.position);
  const weekPath = (period: number) =>
    view.team.isMine
      ? `${base}?period=${period}`
      : `${base}/teams/${view.team.id}?period=${period}`;

  return (
    <section className="fsp-plan-group" aria-label={label}>
      <table className="fsp-plan-table">
        <thead>
          <tr>
            <th scope="col" className="fsp-plan-pos">
              <PositionPill pos={group.position} size="sm" />
            </th>
            {view.weeks.map((w) => (
              <th key={w.period} scope="col">
                <button
                  type="button"
                  className="fsp-plan-week"
                  onClick={() => navigate(weekPath(w.period))}
                  title={`Open ${w.label}'s lineup`}
                >
                  {w.label.replace(/^Week\s+/i, 'Wk ')}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.rows.length === 0 ? (
            <tr>
              <td className="fsp-plan-name fsp-sub" colSpan={view.weeks.length + 1}>
                Nobody rostered
              </td>
            </tr>
          ) : (
            group.rows.map((r) => (
              <tr key={r.player.id} data-reserve={r.reserve || undefined}>
                <th scope="row" className="fsp-plan-name">
                  <button
                    type="button"
                    className="fsp-plan-player"
                    onClick={() =>
                      onOpen({
                        player: r.player,
                        game: null,
                        projection: null,
                        points: null,
                        statLine: null,
                      })
                    }
                  >
                    <span className="fsp-name-text">{r.player.shortName}</span>
                  </button>
                  <ProTeamCode code={r.player.proTeam} />
                  {/* The injury, not this week's bye: the grid says every week's. */}
                  <StatusBadge player={{ ...r.player, onBye: false }} />
                  {r.reserve ? <span className="fsp-sub"> · IR</span> : null}
                </th>
                {r.cells.map((c) => (
                  <td
                    key={c.period}
                    className="fsp-plan-cell"
                    data-state={c.state}
                    data-band={counts && c.matchup ? c.matchup.band : undefined}
                  >
                    {c.state === 'game' && c.matchup && c.opponent ? (
                      // The cell's sentence on a tap as well as a hover: it
                      // was a native title a phone never shows.
                      <Term tip={matchupSentence(c.matchup, group.position, c.opponent)}>
                        {`${c.home ? '' : '@'}${clubLabel(c.opponent)}`}
                      </Term>
                    ) : c.state === 'game' ? (
                      `${c.home ? '' : '@'}${c.opponent === null ? '' : clubLabel(c.opponent)}`
                    ) : c.state === 'bye' ? (
                      'BYE'
                    ) : c.state === 'tbd' ? (
                      '—'
                    ) : (
                      '–'
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {group.starting > 0 ? (
          <tfoot>
            <tr>
              <th scope="row" className="fsp-plan-name fsp-sub">
                <Term tip={CAN_PLAY}>Can play</Term>
              </th>
              {group.able.map((a) => {
                const short = a.count !== null && a.count < group.starting;
                return (
                  <td key={a.period} className="fsp-plan-able" data-short={short || undefined}>
                    {a.count === null
                      ? '—'
                      : counts
                        ? `${a.count}/${group.starting}${short ? ' short' : ''}`
                        : short
                          ? 'short'
                          : '✓'}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </section>
  );
}
